import React, { useEffect, useRef, useLayoutEffect, useCallback, useState } from "react";
import { useTaskInfiniteScroll } from "../../hooks/useTaskInfiniteScroll";
import { NoteCard } from "../Note";
import TaskCard from "./TaskCard";
import './ExpandedStackView.css';

const ExpandedStackView = ({
  job,
  searchTerm,
  hasActiveFilters,
  openAiDialogForJob,
  renderStackedImageIcon,
  selectedRow,
  handleRowClick,
  handleRowDoubleClick,
  handleAddFromRow,
  handleEdit,
  handleDelete,
  handleViewAttachments,
  handleReplyToNote,
  handlePriorityClick,
  handlePriorityMouseEnter,
  handlePriorityMouseLeave,
  handleNoteTextMouseEnter,
  handleNoteTextMouseLeave,
  handleLinkedNoteClick,
  handleLinkedNoteMouseEnter,
  handleLinkedNoteMouseLeave,
  isNoteReply,
  getReplyNoteId,
  isOriginalNoteExists,
  userStatusMap,
  loadingUsers,
  getPriorityValue,
  manuallyUpdatedPriorities,
  shouldShowNotePopup,
  toggleStackExpansion,
  onOpenEdit,
  onCreateTask,
  onOpenSlideshow,
  tasks,
  loadingTasks,
  expandedTasks,
  expandedTask,
  onTaskToggle,
  onTaskClick,
  onTaskEdit,
  onTaskDelete,
  onTaskStatusChange,
  notesByTask,
  loadingNotesByTask,
  onCollapseTask,
  userId,
  fetchTasksForJob,
  activeTab,
  onTabChange,
  TASK_STATUSES,
  ALL_TASKS_TAB,
  isAnyStackExpanded,
  hasAnyTaskExpanded,
  expandedStacks,
  stackedJobs,
  highlightedTaskId,
  highlightedNoteId,
  assigneeFilters,
  jobUsers,
  loadingUsersByJob,
  onAssigneeFilter,
  clearAssigneeFilter,
  fetchNotesForTask,
  loadMoreTaskNotes,
  taskPagination,
  fetchJobPermissionUsers,
  refreshNotes,
  stickyOffsetTop = 0,
  onPriorityUpdate,
}) => {
  
  // ========== UI‑only PINNING (no backend) ==========
  const [pinnedTaskIds, setPinnedTaskIds] = useState([]);

  const togglePin = (taskId) => {
    setPinnedTaskIds(prev => 
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [taskId, ...prev]
    );
  };
  // =================================================

  const expandedTaskPagination = expandedTask ? taskPagination?.[expandedTask.id] : null;
  const hasMoreNotes = expandedTaskPagination?.hasMore || false;
  const isLoadingNotes = loadingNotesByTask[expandedTask?.id] || false;

  const { lastNoteRef } = useTaskInfiniteScroll({
    taskId: expandedTask?.id,
    jobId: job.jobId,
    hasMore: hasMoreNotes,
    loading: isLoadingNotes,
    loadMore: loadMoreTaskNotes,
    enabled: !!expandedTask && !isLoadingNotes,
  });

  const tasksGridRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const savedScrollTopRef = useRef(0);

  const getScrollContainer = (element) => {
    if (!element) return window;
    let parent = element.parentElement;
    while (parent) {
      const overflowY = window.getComputedStyle(parent).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        return parent;
      }
      parent = parent.parentElement;
    }
    return window;
  };

  const handleTaskToggle = useCallback((taskId) => {
    if (!expandedTask) {
      if (tasksGridRef.current) {
        const container = getScrollContainer(tasksGridRef.current);
        scrollContainerRef.current = container;
        savedScrollTopRef.current = container === window ? window.scrollY : container.scrollTop;
      } else {
        scrollContainerRef.current = window;
        savedScrollTopRef.current = window.scrollY;
      }
    }
    onTaskToggle(taskId);
  }, [expandedTask, onTaskToggle]);

  useLayoutEffect(() => {
    if (!expandedTask && savedScrollTopRef.current !== 0) {
      const container = scrollContainerRef.current;
      if (container) {
        if (container === window) window.scrollTo(0, savedScrollTopRef.current);
        else container.scrollTop = savedScrollTopRef.current;
        savedScrollTopRef.current = 0;
      }
    }
  }, [expandedTask]);

  // ========== FETCH NOTES WHEN TASK EXPANDS ==========
  useEffect(() => {
    if (expandedTask && expandedTask.id && fetchNotesForTask) {
      // Check if notes are already loaded or currently loading
      const hasNotes = notesByTask[expandedTask.id] && notesByTask[expandedTask.id].length > 0;
      const isLoading = loadingNotesByTask[expandedTask.id];
      
      if (!hasNotes && !isLoading) {
        console.log("📢 Fetching notes for expanded task:", expandedTask.id);
        fetchNotesForTask(expandedTask.id, expandedTask.jobId);
      }
    }
  }, [expandedTask, fetchNotesForTask, notesByTask, loadingNotesByTask]);

  // ========== HANDLE TASK HIGHLIGHTING ==========
  useEffect(() => {
    if (highlightedTaskId && !expandedTask) {
      console.log("🎯 ExpandedStackView: Highlighting task:", highlightedTaskId);
      
      const timer = setTimeout(() => {
        const taskElement = document.querySelector(`.task-card[data-task-id="${highlightedTaskId}"]`);
        if (taskElement) {
          const rect = taskElement.getBoundingClientRect();
          const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
          
          if (!isInViewport) {
            taskElement.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          
          taskElement.classList.add("task-highlight");
          setTimeout(() => {
            taskElement.classList.remove("task-highlight");
          }, 3000);
        } else {
          console.warn("Task element not found for highlighting:", highlightedTaskId);
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [highlightedTaskId, expandedTask]);

  // ========== HANDLE NOTE HIGHLIGHTING WITH OPTIMIZED RETRIES ==========
  useEffect(() => {
    if (highlightedNoteId && expandedTask) {
      console.log("🎯 ExpandedStackView: Note highlighting triggered for:", highlightedNoteId);
      
      let attempts = 0;
      const maxAttempts = 10;
      let timeoutId = null;
      
      const attemptHighlight = () => {
        const noteElement = document.querySelector(`[data-note-id="${highlightedNoteId}"]`);
        
        if (noteElement) {
          const rect = noteElement.getBoundingClientRect();
          const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
          
          if (!isInViewport) {
            noteElement.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          
          noteElement.classList.add("notification-highlight");
          setTimeout(() => {
            noteElement.classList.remove("notification-highlight");
          }, 3000);
          console.log("✅ Note highlighted successfully:", highlightedNoteId);
          return true;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          timeoutId = setTimeout(attemptHighlight, 300);
        } else {
          console.warn("❌ Failed to find note element after", maxAttempts, "attempts:", highlightedNoteId);
        }
        return false;
      };
      
      timeoutId = setTimeout(attemptHighlight, 200);
      
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [highlightedNoteId, expandedTask]);

  useEffect(() => {
    if (job?.jobId && fetchJobPermissionUsers) fetchJobPermissionUsers(job.jobId);
  }, [job?.jobId, fetchJobPermissionUsers]);

  if (!job) return null;

  const jobName = job.jobName;
  const selectedAssigneeIds = assigneeFilters[job.jobId];
  const taskSummary = tasks.reduce((acc, task) => {
    acc[task.status] = (acc[task.status] || 0) + 1;
    return acc;
  }, {});

  const getFilteredTasks = () => {
    let filteredTasks = tasks;
    if (activeTab === ALL_TASKS_TAB.id) {
      filteredTasks = filteredTasks.filter(t => t.status !== TASK_STATUSES.ARCHIVED.id);
    } else if (activeTab === TASK_STATUSES.ARCHIVED.id) {
      filteredTasks = filteredTasks.filter(t => t.status === TASK_STATUSES.ARCHIVED.id);
    } else {
      filteredTasks = filteredTasks.filter(t => t.status === activeTab);
    }
    if (selectedAssigneeIds?.length) {
      filteredTasks = filteredTasks.filter(task =>
        task.assignedUsers?.some(user => selectedAssigneeIds.includes(user.userId))
      );
    }
    return filteredTasks;
  };

  const filteredTasks = getFilteredTasks();

  // Sort: pinned tasks first, then by latest note timestamp
  const sortedFilteredTasks = [...filteredTasks].sort((a, b) => {
    const aPinned = pinnedTaskIds.includes(a.id);
    const bPinned = pinnedTaskIds.includes(b.id);
    if (aPinned !== bPinned) return aPinned ? -1 : 1;

    const getTaskTime = (task) => {
      if (task.latestNoteTimeStamp) return new Date(task.latestNoteTimeStamp).getTime();
      if (task.createdAt) return new Date(task.createdAt).getTime();
      if (task.updatedAt) return new Date(task.updatedAt).getTime();
      return 0;
    };
    return getTaskTime(b) - getTaskTime(a);
  });

  const allTabs = [ALL_TASKS_TAB, ...Object.values(TASK_STATUSES).sort((a, b) => a.order - b.order)];
  const users = jobUsers[job.jobId] || [];
  const isLoadingUsers = loadingUsersByJob[job.jobId];

  const getEmptyIcon = () => {
    if (activeTab === ALL_TASKS_TAB.id) return "fa-tasks";
    if (activeTab === TASK_STATUSES.ARCHIVED.id) return "fa-archive";
    const status = Object.values(TASK_STATUSES).find(s => s.id === activeTab);
    return status?.icon || "fa-tasks";
  };

  return (
    <div className="es-container">
      <div className="es-stack">
        <div className="es-tasks-container" style={{ paddingTop: expandedTask ? "16px" : undefined }}>
          {loadingTasks ? (
            <div className="es-loading-state">
              <i className="fas fa-spinner fa-spin" />
              <div>Loading tasks for {jobName}...</div>
            </div>
          ) : (
            <>
              {tasks.length === 0 ? (
                <div className="es-empty-state">
                  <i className="fas fa-tasks" />
                  <p>No tasks found for this job</p>
                  <button onClick={() => onCreateTask(job)} className="es-create-btn">
                    <i className="fas fa-plus" /> Create First Task
                  </button>
                </div>
              ) : (
                <>
                  {expandedTask ? (
                    <div className="es-expanded-task">
                      <div className="es-task-notes">
                        {loadingNotesByTask[expandedTask.id] && (!notesByTask[expandedTask.id]?.length) ? (
                          <div className="es-loading-state">
                            <i className="fas fa-spinner fa-spin" />
                            <span>Loading notes for {expandedTask.title}...</span>
                          </div>
                        ) : (
                          <>
                            <div className="es-notes-grid">
                              {notesByTask[expandedTask.id]?.map((note, index) => {
                                const priorityValue = manuallyUpdatedPriorities?.[note.id] ?? note.priority ?? getPriorityValue(note);
                                const noteWithPriority = { ...note, priority: priorityValue, priorityValue };
                                const isLastNote = index === notesByTask[expandedTask.id].length - 1;
                                return (
                                  <div key={`${note.id}-${priorityValue}`} ref={isLastNote ? lastNoteRef : null}>
                                    <NoteCard
                                      userId={userId}
                                      note={noteWithPriority}
                                      selectedRow={selectedRow}
                                      searchTerm={searchTerm}
                                      viewMode="stacked"
                                      handleRowClick={handleRowClick}
                                      handleRowDoubleClick={handleRowDoubleClick}
                                      handleAddFromRow={handleAddFromRow}
                                      handleEdit={handleEdit}
                                      handleDelete={handleDelete}
                                      handleViewAttachments={handleViewAttachments}
                                      handleReplyToNote={handleReplyToNote}
                                      handlePriorityClick={handlePriorityClick}
                                      handlePriorityMouseEnter={handlePriorityMouseEnter}
                                      handlePriorityMouseLeave={handlePriorityMouseLeave}
                                      handleNoteTextMouseEnter={handleNoteTextMouseEnter}
                                      handleNoteTextMouseLeave={handleNoteTextMouseLeave}
                                      handleLinkedNoteClick={handleLinkedNoteClick}
                                      handleLinkedNoteMouseEnter={handleLinkedNoteMouseEnter}
                                      handleLinkedNoteMouseLeave={handleLinkedNoteMouseLeave}
                                      renderCardImageIcon={renderStackedImageIcon}
                                      isNoteReply={isNoteReply}
                                      getReplyNoteId={getReplyNoteId}
                                      isOriginalNoteExists={isOriginalNoteExists}
                                      userStatusMap={userStatusMap}
                                      loadingUsers={loadingUsers}
                                      getPriorityValue={() => priorityValue}
                                      manuallyUpdatedPriorities={manuallyUpdatedPriorities}
                                      shouldShowNotePopup={shouldShowNotePopup}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                            {isLoadingNotes && notesByTask[expandedTask.id]?.length > 0 && hasMoreNotes && (
                              <div className="es-loading-more"><i className="fas fa-spinner fa-spin" /> Loading more notes...</div>
                            )}
                            {notesByTask[expandedTask.id]?.length === 0 && !loadingNotesByTask[expandedTask.id] && (
                              <div className="es-no-notes">No notes yet for this task</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="es-tabs-row" style={{ position: 'sticky', top: '20px', zIndex: 9 }}>
                        <div className="es-tabs">
                          {allTabs.map((status) => {
                            let count = 0;
                            if (status.id === ALL_TASKS_TAB.id) count = tasks.filter(t => t.status !== TASK_STATUSES.ARCHIVED.id).length;
                            else count = taskSummary[status.id] || 0;
                            const isActive = activeTab === status.id;
                            return (
                              <button
                                key={status.id}
                                onClick={() => onTabChange(job.jobId, status.id)}
                                className={`es-tab ${isActive ? 'active' : ''}`}
                                style={{
                                  borderBottom: isActive ? `2px solid ${status.color}` : "2px solid transparent",
                                  color: isActive ? status.color : "#666"
                                }}
                              >
                                <i className={`fas ${status.icon}`} style={{ color: status.color }} />
                                <span className="es-tab-name">{status.name}</span>
                                {count > 0 && (
                                  <span
                                    className="es-tab-count"
                                    style={{
                                      background: isActive ? status.color : "#e9ecef",
                                      color: isActive ? "white" : "#666"
                                    }}
                                  >
                                    {count}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {users.length > 0 && !expandedTask && (
                          <div className="es-assignee-filter">
                            <div className="es-assignee-label"><i className="fas fa-user-check" /></div>
                            <div className="es-assignee-list">
                              {isLoadingUsers ? (
                                <div className="es-assignee-loading"><i className="fas fa-spinner fa-spin" /></div>
                              ) : (
                                <div className="es-assignee-avatars">
                                  {selectedAssigneeIds?.length > 0 && (
                                    <button onClick={() => clearAssigneeFilter(job.jobId)} className="es-clear-filter" title="Clear all filters">
                                      <i className="fas fa-times" />
                                    </button>
                                  )}
                                  {users.slice(0, 8).map((user, index) => (
                                    <button
                                      key={user.id}
                                      onClick={() => onAssigneeFilter(job.jobId, user.id)}
                                      className={`es-assignee-ring ${selectedAssigneeIds?.includes(user.id) ? 'active' : ''}`}
                                      style={{
                                        backgroundColor: selectedAssigneeIds?.includes(user.id) ? "#3498db" : user.color || "#6c757d",
                                        transform: `translateX(${index * -6}px)`,
                                        zIndex: users.length - index,
                                      }}
                                      title={user.name}
                                    >
                                      <span>{user.initials}</span>
                                      {selectedAssigneeIds?.includes(user.id) && <span className="es-active-dot" />}
                                    </button>
                                  ))}
                                  {users.length > 8 && (
                                    <div className="es-more-badge" title={`${users.length - 8} more users`}>
                                      +{users.length - 8}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div ref={tasksGridRef} className="es-tasks-grid">
                        {sortedFilteredTasks.length > 0 ? (
                          sortedFilteredTasks.map(task => (
                            <div key={task.id} className="es-task-card">
                              <TaskCard
                                task={task}
                                onEdit={onTaskEdit}
                                onDelete={onTaskDelete}
                                onStatusChange={onTaskStatusChange}
                                onClick={onTaskClick}
                                onToggle={handleTaskToggle}
                                isExpanded={false}
                                showToggle={true}
                                userId={userId}
                                isHighlighted={highlightedTaskId === task.id}
                                onAddNote={(task) => {
                                  const userFromStorage = JSON.parse(localStorage.getItem("user") || "{}");
                                  handleAddFromRow({
                                    date: new Date().toISOString().split("T")[0],
                                    jobId: job?.jobId || job?.id,
                                    job: job?.jobName || job?.name,
                                    projectId: job?.projectId,
                                    project: job?.projectName || job?.project?.name,
                                    workspaceId: job?.workspaceId,
                                    workspace: job?.workspaceName || job?.workspace || job?.notes?.[0]?.workspace,
                                    taskName: task.title,
                                    taskId: task.id,
                                    taskFriendlyId: task.friendlyId,
                                    userId: userFromStorage.id,
                                    taskDescription: task.description,
                                    taskStatus: task.status,
                                  });
                                }}
                                job={job}
                                fetchTasksForJob={fetchTasksForJob}
                                refreshNotes={refreshNotes}
                                onPriorityUpdate={onPriorityUpdate} 
                                onOpenSlideshow={onOpenSlideshow}
                                isPinned={pinnedTaskIds.includes(task.id)}
                                onTogglePin={() => togglePin(task.id)}
                              />
                            </div>
                          ))
                        ) : (
                          <div className="es-empty-tab-state">
                            <i className={`fas ${getEmptyIcon()}`} />
                            <p>
                              {selectedAssigneeIds?.length > 0
                                ? `No tasks assigned to selected users in ${activeTab === ALL_TASKS_TAB.id ? "this job" : "this status"}`
                                : `No tasks in ${activeTab === ALL_TASKS_TAB.id ? "this job" : "this status"}`}
                            </p>
                            {selectedAssigneeIds?.length > 0 && (
                              <button onClick={() => clearAssigneeFilter(job.jobId)} className="es-clear-filter-btn">
                                Clear All Assignee Filters ({selectedAssigneeIds.length})
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandedStackView;